/**
 * Given a dot-notation entity edit path, walk back to the nearest
 * enclosing array-item segment (a `<propertyName>.<digits>` pair) and
 * return the prefix of the path up to and including that index segment.
 * Returns `undefined` if the path has no array-item ancestor.
 *
 * Nameless by design: a generic control can't name the array it lives
 * inside (the branching control knows nothing about "questions"). It
 * resolves its own enclosing array item — whatever array it was rendered
 * inside.
 *
 * @example
 *   findEnclosingItemPath(
 *     'attributes.domainEntityAttributes.sections.0.questions.2.branching',
 *   )
 *   // => 'attributes.domainEntityAttributes.sections.0.questions.2'
 */
export const findEnclosingItemPath = (
  editPath: string | undefined,
): string | undefined => {
  if (!editPath) return undefined;
  const segments = editPath.split('.');
  // Scan right-to-left for the nearest numeric-index segment that has a
  // (non-numeric) property name before it — that pair is an array item.
  //
  // NOTE: This walker assumes JSONForms hands us dot-notation paths. If
  // JSONForms ever switches to slash-delimited paths (or another
  // separator), the split above will produce a single-segment array and
  // this loop will silently return `undefined` for every call.
  for (let i = segments.length - 1; i >= 1; i -= 1) {
    if (
      /^\d+$/.test(segments[i] ?? '') &&
      !/^\d+$/.test(segments[i - 1] ?? '')
    ) {
      return segments.slice(0, i + 1).join('.');
    }
  }
  return undefined;
};
